"use client";

import { useState, useRef, useEffect, Suspense } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  LoaderCircle,
  CircleAlert,
  CircleCheckBig,
} from "lucide-react";
import { createClient } from "@/lib/supabase/client";

type AuthStage = "email" | "verify" | "success";
type FormState = "idle" | "loading" | "error";

function LoginOTPContent() {
  const router = useRouter();
  const supabase = createClient();

  // State management
  const [stage, setStage] = useState<AuthStage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check if we're in staging environment
  const isStaging = process.env.NEXT_PUBLIC_ENVIRONMENT === "staging";

  // Timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && stage === "verify") {
      setCanResend(true);
    }
  }, [resendTimer, stage]);

  // Email validation
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Send OTP function
  const sendOTP = async () => {
    setFormState("loading");
    setErrorMessage("");

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address");
      setFormState("error");
      return;
    }

    try {
      // In staging, skip actual email sending
      if (isStaging) {
        setStage("verify");
        setFormState("idle");
        setCanResend(false);
        setResendTimer(60);
        // Focus first code input
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
        return;
      }

      // Normal OTP flow
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          shouldCreateUser: false,
        },
      });

      if (error) {
        setFormState("error");

        if (
          error.message.includes("not exist") ||
          error.message.includes("not found")
        ) {
          setErrorMessage(
            "No account found with this email. Please check your email or create a new booking.",
          );
        } else if (error.message.includes("rate limit")) {
          setErrorMessage(
            "Too many attempts. Please wait a few minutes before trying again.",
          );
        } else {
          setErrorMessage(error.message || "Failed to send verification code");
        }
        return;
      }

      // Success - move to verify stage
      setStage("verify");
      setFormState("idle");
      setCanResend(false);
      setResendTimer(60);

      // Focus first code input
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      setFormState("error");
      setErrorMessage("An unexpected error occurred. Please try again.");
    }
  };

  // Handle code input changes
  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split("");
      const newCode = [...code];
      pastedCode.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });
      setCode(newCode);

      // Focus last filled input or next empty one
      const lastFilledIndex = index + pastedCode.length - 1;
      if (lastFilledIndex < 5) {
        inputRefs.current[lastFilledIndex + 1]?.focus();
      } else {
        inputRefs.current[5]?.focus();
      }
    } else {
      // Normal input
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      // Auto-advance to next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  // Handle backspace
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text") || "";
    const digits = pasted.replace(/\D/g, "").slice(0, 6).split("");
    if (!digits.length) return;

    const newCode = [...code];
    digits.forEach((digit, idx) => {
      if (idx < 6) {
        newCode[idx] = digit;
      }
    });
    setCode(newCode);

    const focusIndex = Math.min(digits.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  // Verify OTP code
  const verifyOTP = async () => {
    const fullCode = code.join("");

    if (fullCode.length !== 6) {
      setErrorMessage("Please enter all 6 digits");
      return;
    }

    setFormState("loading");
    setErrorMessage("");

    try {
      // Staging bypass
      if (isStaging && fullCode === "000000") {
        const response = await fetch("/api/auth/azky", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, otp: fullCode }),
        });

        if (response.ok) {
          // Staging bypass creates session server-side, need to sync client
          await handleSuccessfulAuth();

          // Force client to check for the new user (more secure than getSession)
          const {
            data: { user },
          } = await supabase.auth.getUser();
          if (user) {
            // User authenticated, redirect immediately
            router.push("/protected");
          } else {
            // Fallback: refresh the auth state
            await supabase.auth.refreshSession();
            router.push("/protected");
          }
          return;
        } else {
          const errorData = await response.json();
          setFormState("error");
          setErrorMessage(errorData.error || "Staging login failed");
          setCode(["", "", "", "", "", ""]);
          inputRefs.current[0]?.focus();
          return;
        }
      }

      // Normal OTP verification
      const { data, error } = await supabase.auth.verifyOtp({
        email: email,
        token: fullCode,
        type: "email",
      });

      if (error) {
        setFormState("error");

        if (error.message.includes("expired")) {
          setErrorMessage("Code expired. Please request a new one.");
        } else if (error.message.includes("invalid")) {
          setErrorMessage("Invalid code. Please check and try again.");
        } else {
          setErrorMessage(
            error.message || "Verification failed. Please try again.",
          );
        }

        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      if (!data?.session) {
        setFormState("error");
        setErrorMessage(
          "Verification succeeded but no session was created. Please try again.",
        );
        setCode(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      // Normal OTP creates a session, wait for auth state change
      await handleSuccessfulAuth();
    } catch (err) {
      setFormState("error");
      setErrorMessage("An unexpected error occurred. Please try again.");
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_IN") {
          // Session is now guaranteed to be set on the client.
          // Redirect to trigger middleware's role-based routing.
          router.push("/protected");
        }
      },
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [supabase, router]);

  // Handle successful authentication - let middleware handle role-based routing
  const handleSuccessfulAuth = async () => {
    setStage("success");
    setFormState("idle");
    // The onAuthStateChange listener now handles the redirect.
  };

  // Handle resend
  const handleResend = () => {
    if (canResend) {
      sendOTP();
    }
  };

  // Handle back to email
  const handleBack = () => {
    setStage("email");
    setCode(["", "", "", "", "", ""]);
    setErrorMessage("");
    setFormState("idle");
    setCanResend(false);
    setResendTimer(0);
  };

  // Auto-submit when all digits entered
  useEffect(() => {
    if (stage === "verify" && code.every((digit) => digit !== "")) {
      verifyOTP();
    }
  }, [code, stage]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <img
                src="https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/on-air-logo.png"
                alt="On Air Game Shows"
                className="h-14 md:h-16 w-auto p-3 rounded-lg shadow-md"
                style={{
                  backgroundImage:
                    "url(https://onair-prod-assets.s3.eu-west-1.amazonaws.com/app-assets/footer-background-sm.jpg)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
            </div>

            {stage === "email" && (
              <>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Sign in with email
                </h1>
                <p className="text-gray-600 text-sm md:text-base max-w-sm mx-auto">
                  Enter your email to receive a verification code
                </p>
              </>
            )}

            {stage === "verify" && (
              <>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Enter verification code
                </h1>
                <p className="text-gray-600 text-sm md:text-base max-w-sm mx-auto">
                  {isStaging
                    ? "🚧 STAGING MODE: Use code 000000 (no email sent)"
                    : "We've sent a 6-digit code to:"}
                </p>
                <p className="text-gray-900 font-medium text-sm md:text-base mt-1">
                  {email}
                </p>
              </>
            )}

            {stage === "success" && (
              <>
                <CircleCheckBig className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Success!
                </h1>
                <p className="text-gray-600 text-sm md:text-base">
                  Redirecting to your dashboard...
                </p>
              </>
            )}
          </div>

          {/* Email Stage */}
          {stage === "email" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendOTP();
              }}
              className="space-y-6"
            >
              <div>
                <Label
                  htmlFor="email"
                  className="text-sm font-medium mb-2 block"
                >
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  className={`h-12 text-base ${errorMessage ? "border-red-500" : ""}`}
                  disabled={formState === "loading"}
                />
                {errorMessage && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <CircleAlert className="h-4 w-4" />
                    {errorMessage}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-medium"
                disabled={formState === "loading" || !email}
              >
                {formState === "loading" ? (
                  <>
                    <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
                    Sending code...
                  </>
                ) : (
                  "Send verification code"
                )}
              </Button>

              <p className="text-center text-sm text-gray-600 px-4">
                We'll send a 6-digit code to your email for secure access.
              </p>
            </form>
          )}

          {/* Verify Stage */}
          {stage === "verify" && (
            <div className="space-y-6">
              <div className="flex justify-center gap-2">
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) =>
                      handleCodeChange(index, e.target.value.replace(/\D/g, ""))
                    }
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`w-12 h-14 text-center text-2xl font-bold ${
                      errorMessage ? "border-red-500" : ""
                    }`}
                    disabled={formState === "loading"}
                  />
                ))}
              </div>

              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700">
                    <CircleAlert className="h-4 w-4" />
                    <p className="text-sm">{errorMessage}</p>
                  </div>
                </div>
              )}

              {formState === "loading" && (
                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  <span>Verifying...</span>
                </div>
              )}

              {/* Resend Section */}
              <div className="text-center">
                {canResend ? (
                  <button
                    onClick={handleResend}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    disabled={formState === "loading"}
                  >
                    Resend code
                  </button>
                ) : resendTimer > 0 ? (
                  <p className="text-sm text-gray-500">
                    Resend code in {resendTimer} seconds
                  </p>
                ) : null}
              </div>

              {/* Back Button */}
              <div className="text-center">
                <button
                  onClick={handleBack}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                  disabled={formState === "loading"}
                >
                  ← Change email
                </button>
              </div>
            </div>
          )}

          {/* Success Stage */}
          {stage === "success" && (
            <div className="flex justify-center">
              <LoaderCircle className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      {stage !== "success" && (
        <footer className="py-6 px-4">
          <div className="max-w-md mx-auto">
            <Link
              href="/"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to homepage
            </Link>
          </div>
        </footer>
      )}
    </div>
  );
}

export default function LoginOTPPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <LoaderCircle className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        </div>
      }
    >
      <LoginOTPContent />
    </Suspense>
  );
}
